import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd';
import { formatMoney } from '@/utils/format';
import { EpcEstimateSubcontractQuotePanel } from './EpcEstimateSubcontractQuotePanel';
import {
  est3CoverageStatusColor,
  summarizeCoverageByStatus,
} from './est3SubcontractKinds';

const { Text } = Typography;

export interface EpcEstimateSubcontractWorkbenchProps {
  estimateId: string;
  revisionId: string;
  readOnly: boolean;
  packages: any[];
  coverage?: Record<string, unknown> | null;
  loading?: boolean;
  subPkgForm: FormInstance;
  onAddPackage: (values: Record<string, unknown>) => void;
  addPending?: boolean;
  wbsOptions: Array<{ id: string; code: string }>;
  cbsOptions: Array<{ id: string; code: string }>;
  onCostingChanged?: () => void;
}

/**
 * EST3 UX2 Wave 6 — Subcontract packages + quote coverage workbench.
 */
export const EpcEstimateSubcontractWorkbench: React.FC<EpcEstimateSubcontractWorkbenchProps> = ({
  estimateId,
  revisionId,
  readOnly,
  packages,
  coverage,
  loading,
  subPkgForm,
  onAddPackage,
  addPending,
  wbsOptions,
  cbsOptions,
  onCostingChanged,
}) => {
  const [selectedPkgId, setSelectedPkgId] = useState<string | null>(null);

  const selectedPkg = useMemo(
    () => packages.find((p) => p.id === selectedPkgId) || null,
    [packages, selectedPkgId]
  );

  const statusBreakdown = useMemo(
    () => summarizeCoverageByStatus(coverage?.by_status as any),
    [coverage]
  );

  const hasWarnings =
    ((coverage?.validity_warnings as any[]) || []).length > 0 ||
    ((coverage?.coverage_warnings as any[]) || []).length > 0 ||
    Number((coverage?.by_status as any)?.UNQUOTED?.count) > 0;

  return (
    <Card size="small" title="Subcontract & Quote Coverage">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {readOnly ? (
          <Alert type="info" showIcon message="Approved — subcontract read-only" />
        ) : null}

        {coverage ? (
          <Alert
            type={hasWarnings ? 'warning' : 'info'}
            showIcon
            message={
              <Space wrap>
                <Text>
                  Firm coverage: <strong>{Number(coverage.firm_pct ?? 0)}%</strong>
                </Text>
                <Text>
                  Total:{' '}
                  {formatMoney(coverage.total_amount, {
                    display: 'plain',
                    maximumFractionDigits: 0,
                  })}
                </Text>
                <Text>{Number(coverage.package_count ?? packages.length)} packages</Text>
              </Space>
            }
            description={
              (coverage.coverage_warnings as any[])?.length
                ? (coverage.coverage_warnings as any[])
                    .slice(0, 5)
                    .map((w: any) => `${w.name}: ${w.reason}`)
                    .join(' · ')
                : 'Firm / budgetary / historical / manual mix from selected quotes'
            }
          />
        ) : null}

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Statistic title="Packages" value={packages.length} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Firm coverage"
              value={Number(coverage?.firm_pct) || 0}
              suffix="%"
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Subcontract total"
              value={Number(coverage?.total_amount) || 0}
              formatter={(v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })}
            />
          </Col>
        </Row>

        {statusBreakdown.length > 0 ? (
          <Card type="inner" size="small" title="Coverage mix">
            <Space wrap>
              {statusBreakdown.map((row) => (
                <Tag key={row.status} color={est3CoverageStatusColor(row.status)}>
                  {row.status}: {row.count} · {row.pct}%
                </Tag>
              ))}
            </Space>
          </Card>
        ) : null}

        {!readOnly ? (
          <Card type="inner" size="small" title="Add subcontract package">
            <Form form={subPkgForm} layout="inline" onFinish={onAddPackage}>
              <Form.Item name="code">
                <Input placeholder="Code" style={{ width: 90 }} />
              </Form.Item>
              <Form.Item name="name" rules={[{ required: true }]}>
                <Input placeholder="Package name" style={{ width: 160 }} />
              </Form.Item>
              <Form.Item name="wbsNodeId" rules={[{ required: true }]}>
                <Select
                  placeholder="WBS"
                  style={{ width: 110 }}
                  options={wbsOptions.map((n) => ({ value: n.id, label: n.code }))}
                />
              </Form.Item>
              <Form.Item name="cbsCodeId">
                <Select
                  allowClear
                  placeholder="CBS (SUB)"
                  style={{ width: 120 }}
                  options={cbsOptions.map((n) => ({ value: n.id, label: n.code }))}
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addPending}>
                Add package
              </Button>
            </Form>
          </Card>
        ) : null}

        <Row gutter={16}>
          <Col xs={24} lg={10}>
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              dataSource={packages}
              pagination={packages.length > 25 ? { pageSize: 25 } : false}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedPkgId ? [selectedPkgId] : [],
                onChange: (keys) => setSelectedPkgId((keys[0] as string) || null),
              }}
              onRow={(row) => ({
                onClick: () => setSelectedPkgId(row.id),
                style: { cursor: 'pointer' },
              })}
              columns={[
                { title: 'Code', dataIndex: 'code', width: 80, ellipsis: true },
                { title: 'Name', dataIndex: 'name', ellipsis: true },
                {
                  title: 'Coverage',
                  dataIndex: 'coverage_status',
                  width: 100,
                  render: (v) => <Tag color={est3CoverageStatusColor(String(v))}>{v}</Tag>,
                },
                {
                  title: 'Amount',
                  dataIndex: 'estimated_amount',
                  width: 100,
                  render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
                },
                { title: 'Quotes', dataIndex: 'quote_count', width: 60 },
              ]}
            />
          </Col>
          <Col xs={24} lg={14}>
            <EpcEstimateSubcontractQuotePanel
              estimateId={estimateId}
              revisionId={revisionId}
              packageId={selectedPkgId}
              packageRow={selectedPkg}
              readOnly={readOnly}
              onChanged={onCostingChanged}
            />
          </Col>
        </Row>
      </Space>
    </Card>
  );
};
